import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { FloorType } from 'src/app/models/floorType.model';
import { FloorTypes } from 'src/app/enums/floorTypes.enum';
import { PriceService } from 'src/app/services/price.service';
import { LanguageService } from 'src/app/services/language.service';
import { StartupService } from 'src/app/services/startup.service';
import { ThreeDService } from 'src/app/services/three-d.service';
import { ProductClassificationService } from 'src/app/services/product-classification.service';
import { ProductClass } from 'src/app/enums/productClass.enum';

@Component({
  selector: 'app-step-four',
  templateUrl: './step-four.component.html',
  styleUrls: ['./step-four.component.scss'],
})
export class StepFourComponent implements OnInit, OnDestroy {
  floorTypes: FloorType[] = [];
  displayFloorTypes: FloorType[] = [];
  private _wantsFloor = true;
  private _strongFloor = false;
  plint = true;
  ventilationProfiles: boolean;
  sunkenFloor = false;
  plinthsInside: boolean;
  plinthsOutside: boolean;
  ceilingMoldings = false;
  pricesVisible: boolean;
  canHavePinths = false;
  constructor(
    private configurationService: ConfigurationService,
    private priceService: PriceService,
    private languageService: LanguageService,
    private startupService: StartupService,
    private threeDService: ThreeDService,
    private productClassificationService: ProductClassificationService
  ) {}

  ngOnInit() {
    this.startupService.priceEnabledSetting$.subscribe((enabled: boolean) => {
      this.pricesVisible = enabled;
    });
    this.canHavePinths = this.configurationService.canHavePlinths;
    this._wantsFloor = this.configurationService.floorType !== FloorTypes.NONE;
    this._strongFloor = false;
    this.floorTypes = this.configurationService.allowedFloorTypes.filter(t => t.available);
    this.floorTypes.forEach(ft => (ft.selected$ = new EventEmitter<boolean>()));
    this.sunkenFloor = this.configurationService.sunkenFloor;
    this.plinthsInside = this.configurationService.plinthsInside;
    this.plinthsOutside = this.configurationService.plinthsOutside;
    this.ceilingMoldings = this.configurationService.ceilingMoldings;

    this.productClassificationService.SetFloorClassification(this.floorTypes);

    if (this.configurationService.floorType !== undefined && this.configurationService.floorType !== null) {
      this.floorTypes.forEach(f => (f.selected = false));
      const floorTypeToUpdate = this.floorTypes.find(f => f.option === this.configurationService.floorType);
      if (floorTypeToUpdate) {
        floorTypeToUpdate.selected = true;
        const floorProductClassification = this.productClassificationService.GetFloorClassification(this.configurationService.floorType);
        this.configurationService.productClass = floorProductClassification;
      }
    }
    if (this.floorTypes.some(f => f.selected && f.strong)) {
      this._strongFloor = true;
    }

    this.ventilationProfiles = this.configurationService.ventilationProfiles;

    this.displayFloorTypes = this.floorTypes.filter(
      f => f.strong === this._strongFloor && f.minimumWallThickness <= this.configurationService.wallThickness
    );
    this.updatePricesForFloorTypes();
    this.processSelectedFloorType(this.floorTypes.find(f => f.selected === true));

    this.configurationService.setHideFrontWalls(true);
    this.threeDService.setZoom(3.75);
    this.threeDService.resetDefaultCamera();
  }

  ngOnDestroy() {
    this.configurationService.setHideFrontWalls(false);
  }

  get canDisableFloor(): boolean {
    return !this.configurationService.mustHaveFloor;
  }

  get currentLocale() {
    return this.languageService.currentLanguage;
  }

  get wantsFloor(): boolean {
    return this._wantsFloor;
  }

  set wantsFloor(value: boolean) {
    if (this._wantsFloor !== value) {
      this._wantsFloor = value;

      if (!this._wantsFloor) {
        this.strongFloor = false;
        this.configurationService.floorType = FloorTypes.NONE;
        this.configurationService.plinthsInside = false;
        this.configurationService.plinthsOutside = false;
        this.configurationService.sunkenFloor = false;
        this.configurationService.ceilingMoldings = false;
        this.configurationService.ventilationProfiles = false;
        this.configurationService.productClass = ProductClass.STANDARD;
      } else {
        this.setVentilationProfiles(true);
        this.setPlinthsInside(this.plinthsInside);
        if (this._strongFloor) {
          this.configurationService.floorType = FloorTypes.ALUMINUM25;
        } else {
          this.configurationService.floorType = FloorTypes.DURAFLOOR;
        }
        this.ventilationProfiles = this.configurationService.ventilationProfiles;
        this.updatePricesForFloorTypes();
        this.setSunkenFloor(false);
      }
    }
  }

  processSelectedFloorType(floorType: FloorType) {
    if (floorType) {
      this.configurationService.floorType = FloorTypes[floorType.option];
      this.floorTypes.forEach(ft => (ft.selected = false));
      this.floorTypes.find(a => a.option === floorType.option).selected = true;
      floorType.selected$.emit();
      this.updatePricesForFloorTypes();
      this.configurationService.productClass = this.productClassificationService.GetFloorClassification(floorType.option);
    }
  }

  get strongFloor(): boolean {
    return this._strongFloor;
  }

  set strongFloor(value: boolean) {
    if (this._strongFloor !== value) {
      this._strongFloor = value;
      this.displayFloorTypes = this.floorTypes.filter(
        f => f.strong === this._strongFloor && f.minimumWallThickness <= this.configurationService.wallThickness
      );
      if (value) {
        this.configurationService.floorType = FloorTypes.ALUMINUM25;
      } else {
        this.configurationService.floorType = FloorTypes.DURAFLOOR;
      }
      this.updatePricesForFloorTypes();
    }
  }

  private updatePricesForFloorTypes() {
    this.displayFloorTypes.forEach(ft => {
      ft.priceChange = this.computePriceDifferenceForFloors(ft.option as FloorTypes);
    });
  }

  setVentilationProfiles(event: boolean) {
    this.configurationService.ventilationProfiles = event;
  }

  setSunkenFloor(value: boolean) {
    this.configurationService.sunkenFloor = value;
    this.sunkenFloor = value;
  }

  setPlinthsInside(value: boolean) {
    this.configurationService.plinthsInside = value;
    this.plinthsInside = value;
  }

  setPlinthsOutside(value: boolean) {
    this.configurationService.plinthsOutside = value;
  }

  setCeilingMoldings(value: boolean) {
    this.configurationService.ceilingMoldings = value;
  }

  computePriceDifferenceForFloors(newFloor: FloorTypes): number | false {
    if (!this.pricesVisible) {
      return false;
    }
    const configuration = this.configurationService.configuration;
    const newConfig = configuration.clone();

    newConfig.settings.floorType = newFloor;
    const oldPrice = this.priceService.calculateTotalPriceExcludingDoors(configuration);
    const newPrice = this.priceService.calculateTotalPriceExcludingDoors(newConfig);
    if (oldPrice === false || newPrice === false) {
      return false;
    }
    return newPrice - oldPrice;
  }

  get priceDifferenceForPlinthsInside(): number | false {
    if (!this.pricesVisible) {
      return false;
    }
    const configuration = this.configurationService.configuration;
    const newConfig = configuration.clone();

    newConfig.settings.plinthsInside = !configuration.settings.plinthsInside;
    const oldPrice = this.priceService.calculateTotalPriceExcludingDoors(configuration);
    const newPrice = this.priceService.calculateTotalPriceExcludingDoors(newConfig);
    if (oldPrice === false || newPrice === false) {
      return false;
    }
    return newPrice - oldPrice;
  }

  get priceDifferenceForPlinthsOutside(): number | false {
    if (!this.pricesVisible) {
      return false;
    }
    const configuration = this.configurationService.configuration;
    const newConfig = configuration.clone();

    newConfig.settings.plinthsOutside = !configuration.settings.plinthsOutside;
    const oldPrice = this.priceService.calculateTotalPriceExcludingDoors(configuration);
    const newPrice = this.priceService.calculateTotalPriceExcludingDoors(newConfig);
    if (oldPrice === false || newPrice === false) {
      return false;
    }
    return newPrice - oldPrice;
  }

  get priceDifferenceForVentilationProfiles(): number | false {
    if (!this.pricesVisible) {
      return false;
    }
    const configuration = this.configurationService.configuration;
    const newConfig = configuration.clone();

    newConfig.settings.ventilationProfiles = !configuration.settings.ventilationProfiles;
    const oldPrice = this.priceService.calculateTotalPriceExcludingDoors(configuration);
    const newPrice = this.priceService.calculateTotalPriceExcludingDoors(newConfig);
    if (oldPrice === false || newPrice === false) {
      return false;
    }
    return newPrice - oldPrice;
  }

  get priceDifferenceForCeilingMoldings(): number | false {
    if (!this.pricesVisible) {
      return false;
    }
    const configuration = this.configurationService.configuration;
    const newConfig = configuration.clone();

    newConfig.settings.ceilingMoldings = !configuration.settings.ceilingMoldings;
    const oldPrice = this.priceService.calculateTotalPriceExcludingDoors(configuration);
    const newPrice = this.priceService.calculateTotalPriceExcludingDoors(newConfig);
    if (oldPrice === false || newPrice === false) {
      return false;
    }
    return newPrice - oldPrice;
  }

  get priceDifferenceForYesFloor(): number | false {
    if (!this.pricesVisible) {
      return false;
    }
    if (this.wantsFloor) {
      return 0;
    }
    const configuration = this.configurationService.configuration;
    const newConfig = configuration.clone();
    newConfig.settings.floorType = FloorTypes.DURAFLOOR;
    newConfig.settings.ventilationProfiles = true;

    const oldPrice = this.priceService.calculateTotalPriceExcludingDoors(configuration);
    const newPrice = this.priceService.calculateTotalPriceExcludingDoors(newConfig);
    if (oldPrice === false || newPrice === false) {
      return false;
    }
    return newPrice - oldPrice;
  }

  get priceDifferenceForNoFloor(): number | false {
    if (!this.pricesVisible) {
      return false;
    }
    if (!this.wantsFloor) {
      return 0;
    }
    const configuration = this.configurationService.configuration;
    const newConfig = configuration.clone();
    newConfig.settings.floorType = FloorTypes.NONE;
    newConfig.settings.plinthsInside = false;
    newConfig.settings.plinthsOutside = false;
    newConfig.settings.sunkenFloor = false;
    newConfig.settings.ceilingMoldings = false;
    newConfig.settings.ventilationProfiles = false;
    const oldPrice = this.priceService.calculateTotalPriceExcludingDoors(configuration);
    const newPrice = this.priceService.calculateTotalPriceExcludingDoors(newConfig);
    if (oldPrice === false || newPrice === false) {
      return false;
    }
    return newPrice - oldPrice;
  }

  get priceDifferenceForYesFloorReinforced(): number | false {
    if (!this.pricesVisible) {
      return false;
    }
    if (this.strongFloor) {
      return 0;
    }
    const configuration = this.configurationService.configuration;
    const newConfig = configuration.clone();
    newConfig.settings.floorType = FloorTypes.ALUMINUM25;
    const oldPrice = this.priceService.calculateTotalPriceExcludingDoors(configuration);
    const newPrice = this.priceService.calculateTotalPriceExcludingDoors(newConfig);
    if (oldPrice === false || newPrice === false) {
      return false;
    }
    return newPrice - oldPrice;
  }

  get priceDifferenceForNoFloorReinforced(): number | false {
    if (!this.pricesVisible) {
      return false;
    }
    if (!this.strongFloor) {
      return 0;
    }
    const configuration = this.configurationService.configuration;
    const newConfig = configuration.clone();
    newConfig.settings.floorType = FloorTypes.DURAFLOOR;
    const oldPrice = this.priceService.calculateTotalPriceExcludingDoors(configuration);
    const newPrice = this.priceService.calculateTotalPriceExcludingDoors(newConfig);
    if (oldPrice === false || newPrice === false) {
      return false;
    }
    return newPrice - oldPrice;
  }
}
